import * as React from "react"
import * as d3 from 'd3';
import { useGetStatsQuery } from "../redux/stats";
import { connVsContract } from "./types/stats";

import ExportExcel from '../common/ExportExcel';
import { Stack, Typography, Alert, Box, useTheme } from "@mui/material";
import { getFormatedStringFromDate } from "../utils/dateUtils";


const getTitle = (lastUpdateDate: Date): React.ReactElement => (
    <>
        <br />
        This chart shows from last 2 years (Last updated: <b>{getFormatedStringFromDate(lastUpdateDate)}</b>):
        <ul>
            <li>Sales ? how many Lectra cutters in production <span style={{ color: '#BE1622', fontWeight: 'bold' }}>▬▬</span>
                , how many cutters have contract <span style={{ color: '#F3636D', fontWeight: 'bold' }}>▬▬</span>
                &nbsp;or subscription <span style={{ color: '#C2185B', fontWeight: 'bold' }}>▬▬</span></li>
            <li>Support ? how many cutters are connected to Cutterreport <span style={{ color: '#1776C3', fontWeight: 'bold' }}>▬▬</span>
                &nbsp;and Lisa <span style={{ color: '#5BA4D4', fontWeight: 'bold' }}>▬▬</span></li>
            <li>Service ? Dashboard <span style={{ color: '#3F9B91', fontWeight: 'bold' }}>▬▬</span></li>
        </ul>
    </>)

interface D3ChartProps {
    data: connVsContract[];
}

const D3Chart: React.FC<D3ChartProps> = ({ data }) => {
    const svgRef = React.useRef<SVGSVGElement>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const theme = useTheme();

    React.useEffect(() => {
        if (!svgRef.current || !containerRef.current || !data || data.length === 0) return;

        // Clear previous chart
        d3.select(svgRef.current).selectAll("*").remove();

        // Get container dimensions
        const containerWidth = containerRef.current.clientWidth;
        const containerHeight = containerRef.current.clientHeight;

        // Set up dimensions and margins
        const margin = { top: 60, right: 10, bottom: 50, left: 80 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr('width', containerWidth)
            .attr('height', containerHeight)
            .append('g')
            .attr('transform', `translate(${margin.left},${margin.top})`);

        // Create scales
        const xScale = d3.scalePoint()
            .domain(data.map(d => d.YearWeek))
            .range([0, width]);

        const maxY = d3.max(data, d => Math.max(d.Cutters ?? 0, d.Contract ?? 0, d.Subscription ?? 0)) ?? 6500;
        const yScale = d3.scaleLinear()
            .domain([1300, Math.ceil(maxY * 1.05)])
            .range([height, 0]);

        // Add grid
        svg.append('g')
            .attr('class', 'grid')
            .selectAll('line')
            .data(yScale.ticks(10))
            .join('line')
            .attr('x1', 0)
            .attr('x2', width)
            .attr('y1', d => yScale(d))
            .attr('y2', d => yScale(d))
            .attr('stroke', theme.palette.divider)
            .attr('stroke-dasharray', '1,1');

        svg.append('g')
            .attr('class', 'grid')
            .selectAll('line.vertical')
            .data(data.map(d => d.YearWeek))
            .join('line')
            .attr('x1', d => xScale(d)!)
            .attr('x2', d => xScale(d)!)
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.divider)
            .attr('stroke-dasharray', '1,1');

        // Add background
        svg.insert('rect', ':first-child')
            .attr('width', width)
            .attr('height', height)
            .attr('fill', theme.palette.mode === 'light' ? '#fafafa' : theme.palette.background.default);

        // Add X axis
        svg.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(d3.axisBottom(xScale).tickValues(
                xScale.domain().filter((d, i) => i % Math.ceil(data.length / 15) === 0)
            ))
            .selectAll('text')
            .attr('transform', 'rotate(-45)')
            .style('text-anchor', 'end')
            .style('fill', theme.palette.text.primary);

        // Style axis lines and ticks
        svg.selectAll('.domain, .tick line')
            .style('stroke', theme.palette.text.secondary);

        // Add Y axis
        svg.append('g')
            .call(d3.axisLeft(yScale))
            .selectAll('text')
            .style('fill', theme.palette.text.primary);

        // Add Y axis label
        svg.append('text')
            .attr('transform', 'rotate(-90)')
            .attr('y', 0 - margin.left)
            .attr('x', 0 - (height / 2))
            .attr('dy', '1em')
            .style('text-anchor', 'middle')
            .style('fill', theme.palette.text.primary)
            .text('Cutter count');

        // Line generator
        const lineGenerator = (key: keyof connVsContract): d3.Line<connVsContract> => d3.line<connVsContract>()
            .x(d => xScale(d.YearWeek)!)
            .y(d => yScale((d[key] as number) ?? 0))
            .curve(d3.curveMonotoneX);

        // Define lines data
        const lines = [
            { key: 'Cutters' as keyof connVsContract, name: 'Cutters', color: '#BE1622', strokeWidth: 2.5 },
            // { key: 'Contract' as keyof connVsContract, name: 'Contract', color: '#F3636D', strokeWidth: 1 },
            { key: 'Subscription' as keyof connVsContract, name: 'Subscription', color: '#C2185B', strokeWidth: 1, strokeDasharray: '6,3' },
            { key: 'Reports' as keyof connVsContract, name: 'Zip Reports', color: '#1776C3', strokeWidth: 1 },
            { key: 'Lisa' as keyof connVsContract, name: 'Lisa import', color: '#5BA4D4', strokeWidth: 1 },
            { key: 'Dashboard' as keyof connVsContract, name: 'Dashboard', color: '#3F9B91', strokeWidth: 1 }
        ];

        // Draw lines with animation
        lines.forEach((lineConfig, index) => {
            const path = svg.append('path')
                .datum(data)
                .attr('fill', 'none')
                .attr('stroke', lineConfig.color)
                .attr('stroke-width', lineConfig.strokeWidth)
                .attr('stroke-dasharray', (lineConfig as any).strokeDasharray ?? null)
                .attr('d', lineGenerator(lineConfig.key));

            // Animate the line
            const totalLength = path.node()?.getTotalLength() ?? 0;
            path
                .attr('stroke-dasharray', `${totalLength} ${totalLength}`)
                .attr('stroke-dashoffset', totalLength)
                .transition()
                .duration(3000)
                .ease(d3.easeLinear)
                .attr('stroke-dashoffset', 0);
        });

        // Add legend
        const legend = svg.append('g')
            .attr('class', 'legend')
            .attr('transform', `translate(0, -40)`);

        lines.forEach((lineConfig, i) => {
            const legendRow = legend.append('g')
                .attr('transform', `translate(${i * 110}, 0)`);

            legendRow.append('line')
                .attr('x1', 0)
                .attr('x2', 30)
                .attr('y1', 10)
                .attr('y2', 10)
                .attr('stroke', lineConfig.color)
                .attr('stroke-width', lineConfig.strokeWidth)
                .attr('stroke-dasharray', (lineConfig as any).strokeDasharray ?? null);

            legendRow.append('text')
                .attr('x', 35)
                .attr('y', 15)
                .style('font-size', '12px')
                .style('fill', '#666')
                .text(lineConfig.name);
        });

        // Add tooltip
        const tooltip = d3.select(containerRef.current)
            .append('div')
            .style('position', 'absolute')
            .style('background', 'rgba(0, 0, 0, 0.9)')
            .style('color', 'white')
            .style('border', '1px solid rgba(255, 255, 255, 0.2)')
            .style('border-radius', '4px')
            .style('padding', '8px')
            .style('pointer-events', 'none')
            .style('opacity', 0)
            .style('font-size', '12px')
            .style('z-index', 1000);

        // Add invisible overlay for tooltip
        svg.append('rect')
            .attr('width', width)
            .attr('height', height)
            .attr('fill', 'none')
            .attr('pointer-events', 'all')
            .on('mousemove', function(event) {
                const [mouseX] = d3.pointer(event);
                const xValue = xScale.domain()[Math.round(mouseX / (width / (data.length - 1)))];
                const dataPoint = data.find(d => d.YearWeek === xValue);
                
                if (dataPoint) {
                    const containerRect = containerRef.current!.getBoundingClientRect();
                    const mouseXRelative = event.pageX - containerRect.left;
                    const tooltipWidth = 180; // Approximate tooltip width
                    
                    // Position tooltip to the left if too close to right edge
                    const tooltipLeft = mouseXRelative + tooltipWidth + 10 > containerRect.width
                        ? mouseXRelative - tooltipWidth - 10
                        : mouseXRelative + 10;
                    
                    tooltip
                        .style('opacity', 1)
                        .style('left', `${tooltipLeft}px`)
                        .style('top', `${event.pageY - containerRect.top - 10}px`)
                        .html(`
                            <strong>${dataPoint.YearWeek}</strong><br/>
                            <span style="color: #BE1622;">●</span> Cutters: ${dataPoint.Cutters ?? 0}<br/>
                            <span style="color: #F3636D;">●</span> Contract: ${dataPoint.Contract ?? 0}<br/>
                            <span style="color: #C2185B;">●</span> Subscription: ${dataPoint.Subscription ?? 0}<br/>
                            <span style="color: #1776C3;">●</span> Zip Reports: ${dataPoint.Reports ?? 0}<br/>
                            <span style="color: #5BA4D4;">●</span> Lisa import: ${dataPoint.Lisa ?? 0}<br/>
                            <span style="color: #3F9B91;">●</span> Dashboard: ${dataPoint.Dashboard ?? 0}
                        `);
                }
            })
            .on('mouseout', () => {
                tooltip.style('opacity', 0);
            });

        // Cleanup tooltip on unmount
        return () => {
            tooltip.remove();
        };

    }, [data, theme]);

    return (
        <Box ref={containerRef} sx={{ position: 'relative', width: '95%', height: '100%', margin: '0 auto' }}>
            <svg ref={svgRef} style={{ display: 'block' }}></svg>
        </Box>
    );
};

export const ConnVsContractCtrl: React.FC = () => {
    const results: ReturnType<typeof useGetStatsQuery> = useGetStatsQuery('Connexions_versus_Contracts', { skip: false });

    switch (results.status) {
        case 'pending':
            return <Alert severity="info">Loading chart data...</Alert>;
        case 'fulfilled':
            return (
                <>
                    <Stack sx={{ marginX: '1rem' }} direction="row" alignItems="flex-start" justifyContent="space-between">
                        <Typography component="div" fontWeight='bold' fontSize='0.8rem' variant="body2" >
                            {getTitle(results.data.createdAt)}
                        </Typography>
                        <ExportExcel label={'connVsContract'} total={results.data.reportData.length} results={results.data.reportData}
                            svg={{ width: '2rem', fill: '#0E796E' }}
                            sx={{ margin: 'auto' }}
                            title={`Export ${results.data.reportData.length} 'connVsContract' results`} />
                    </Stack>
                    <Typography component="div" sx={{ color: 'warning', marginX: '2rem' }} fontWeight='bold' fontSize='0.8rem' variant="body2" >
                        Data might be reajusted over time, by completion or request tuning.<br />
                        Cutters that have no LISA report activity <b>in the last 10 years</b> are excluded.
                    </Typography>

                    <Box sx={{ width: '100%', height: '600px' }}>
                        <D3Chart data={results.data.reportData} />
                    </Box>
                </>
            )
        default:
            return <Alert severity="error">Failed to load chart data.</Alert>;
    }
};
